import { Download, GraduationCap } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { IDENTITY, SUMMARY, PHILOSOPHY, EXPERIENCE, EDUCATION } from "@/content";

export const About = () => (
  <section
    id="about"
    data-testid="about-section"
    className="scroll-mt-24 px-6 py-24 sm:px-12 lg:px-20 lg:py-32"
  >
    <div className="mx-auto max-w-7xl">
      <SectionHead label="01 — About" title="Strategy with a pulse, analytics with a point of view." testId="about-heading" />

      <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
        <Reveal className="lg:col-span-5" delay={0.1}>
          <div className="img-texture relative overflow-hidden rounded-[2rem] border border-border/60 shadow-xl">
            <img
              src={IDENTITY.aboutPhoto}
              alt={`Candid photo of ${IDENTITY.name}`}
              className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
              data-testid="about-photo"
            />
            <div className="absolute bottom-4 left-4 rounded-full bg-background/85 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground backdrop-blur-md">
              Off-camera, on-brand
            </div>
          </div>
        </Reveal>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <p className="max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg" data-testid="about-bio">
              {SUMMARY} I specialise in social media management, content strategy,
              video production and campaign analytics — bridging brand storytelling
              with the numbers that prove it works.
            </p>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="about-philosophy">
            {PHILOSOPHY.map((p, i) => (
              <Reveal key={p.title} delay={0.2 + i * 0.1}>
                <div className="h-full rounded-2xl border border-border/60 bg-card/60 p-5 transition-colors duration-300 hover:border-terra/50">
                  <p className="mono-label mb-3">0{i + 1}</p>
                  <h3 className="mb-2 font-semibold">{p.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.25}>
            <a
              data-testid="about-resume-button"
              href={IDENTITY.resumeUrl}
              download="Aditi Gajbhiye Resume.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-terra px-6 py-3 text-xs font-semibold uppercase tracking-wider text-primary-foreground transition-transform duration-300 hover:scale-[1.04]"
            >
              <Download size={15} />
              Download Resume
            </a>
          </Reveal>
        </div>
      </div>

      <div className="mt-20 grid grid-cols-1 gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7" data-testid="about-experience">
          <Reveal>
            <h3 className="mb-8 font-serif text-2xl tracking-tight">Experience</h3>
          </Reveal>
          <div className="space-y-8 border-l border-border/70 pl-7">
            {EXPERIENCE.map((e, i) => (
              <Reveal key={e.role} delay={i * 0.08}>
                <div className="relative" data-testid={`experience-item-${i}`}>
                  <span className="absolute -left-[31px] top-1.5 h-2.5 w-2.5 rounded-full bg-terra" />
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-terra">{e.period}</p>
                  <h4 className="mt-1 font-semibold">{e.role}</h4>
                  <p className="text-sm text-muted-foreground">{e.org}</p>
                  <ul className="mt-2 space-y-1">
                    {e.points.map((pt) => (
                      <li key={pt} className="text-sm leading-relaxed text-muted-foreground">
                        — {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5" data-testid="about-education">
          <Reveal>
            <h3 className="mb-8 font-serif text-2xl tracking-tight">Education</h3>
          </Reveal>
          <div className="space-y-5">
            {EDUCATION.map((ed, i) => (
              <Reveal key={ed.degree} delay={i * 0.1}>
                <div className="rounded-2xl border border-border/60 bg-card/60 p-6" data-testid={`education-item-${i}`}>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <GraduationCap size={18} className="shrink-0 text-terra" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                      {ed.period}
                    </span>
                  </div>
                  <h4 className="font-semibold">{ed.degree}</h4>
                  <p className="mt-1 text-sm text-muted-foreground">{ed.org}</p>
                  <p className="mt-2 text-xs text-muted-foreground/80">{ed.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);
